import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { generateCalculation } from "app/global/calculation";
import { DocumentFieldEmitterService } from "app/global/documentfield-emitter.service";
import { applyRelations } from "app/global/fieldrelation";
import { onFieldValidation } from "app/global/fieldvalidation";
import { GlobalService } from "app/global/global.service";
import { IntegrationService } from "app/global/integrations/integrations.service";
import { applyValueToField } from "app/global/synchronize";
import { ValidationHandlerService } from "app/global/validation-handler/validation-handler.service";
import { FieldInfo } from "app/interfaces/field-base";
import { DroplistOptions, Field } from "app/interfaces/fields";
import { UserTimezone } from "app/main/signing-pages/signing-page/signing-page.dto";
import { MomentPipe } from "app/pipes/moment.pipe";
import { lastValueFrom, Observable } from "rxjs";
import Swal from "sweetalert2";
import { DocumentHelperFacade } from "../../documents/document-right-sidebar/document-helper.facade";
import { FieldRenderService } from "../../edit-template/field-render.service";
import { IFieldVisibilityType } from "../../edit-template/field-visibility.dto";
import { ISearcableMetaData } from "../../general-modals/search-meta-data-modal/search-meta-data-modal.component";
import { IField } from "../../settings/documenttype/document-type-settings-modal/document-type-settings.dto";
import { FieldTypes } from "../general-popover-settings/general-popover-settings.dto";
import { FieldsBuilderService } from "./fields-builder.service";
import { UUID } from "./uuid-generator";

declare let $;

@Injectable({
    providedIn: 'root'
})


export class FieldsDataHelperService {


    protected renderer: Renderer2;

    constructor(
        private _globalService: GlobalService,
        private _documentHelperFacade: DocumentHelperFacade,
        private _documentFieldsEmitter: DocumentFieldEmitterService,
        private _momentPipe$: MomentPipe,
        rendererFactory: RendererFactory2,
        private _fieldsBuilderService: FieldsBuilderService,
        private _translocoService: TranslocoService,
        private _integrationService: IntegrationService,
        private _fieldRendererService: FieldRenderService,
        private validationHandlerService: ValidationHandlerService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }



    /**
     * Updates the input field data for a given field identified by its UUID.
     *
     * @param uuid - The unique identifier (string or number) of the field to update.
     * @param textfield - The field data object containing properties to update, of type `FieldTypes`.
     * @param isRecipientField - An optional object containing recipient-specific field overrides.
     * @param fieldSelectedType - The selected field type identifier as a string.
     *
     * @remarks
     * This method constructs an updated field object using properties from the provided `textfield` and `isRecipientField` objects,
     * then delegates the update operation to the global service. It normalizes and defaults several field properties to ensure
     * consistency and compatibility with the backend.
     */
    updateInputFieldsData(uuid: string | number, textfield: FieldTypes, isRecipientField = {}, fieldSelectedType: string, suppressRefresh: boolean = false): void {
        this._globalService.updateField(uuid,
            {
                selected_field_type_id: textfield.id,
                selected_type: textfield.name.toLowerCase().replace(/ /g, "_"),
                select_type_text: textfield.name,
                value: textfield.value,
                field_info: textfield.field_info,
                field_required: (textfield.field_required ?? 0),
                api_attribute: (textfield.api_attribute ?? ''),
                prefixid: fieldSelectedType,
                selected_field_role_id: (textfield.role_id ?? ''),
                recipient_id: (textfield.role_data?.recipient_id ?? 0),
                field_section: textfield.field_section,
                placeholder: textfield.name,
                validation: (textfield.validation ?? {}),
                name: textfield.name,
                description: (textfield?.description ?? ''),
                source: (textfield?.source ?? 'SimpleSign'),
                ...isRecipientField,
                allow_multi_lines: textfield.multiple_line_field,
                is_locked: (textfield?.is_locked ? 1 : 0),
                content_library_selected_field_type: textfield?.key,
                content_library_selected_role: textfield?.role_id ?? '',
                api_field: textfield?.api_field ?? 0,
                api_url: textfield.api_url ?? '',
                api_key: textfield.api_key ?? '',
                secret_hidden_field: textfield.secret_hidden_field ?? 0,
                semi_hidden_field: textfield?.semi_hidden_field || 0,
                pinned_field: textfield?.pinned_field || 0
            }, () => { }, true);
        
        if (!suppressRefresh) {
            this._documentFieldsEmitter.patchData({ refresh: 1 });
        }
    }






    /**
     * Updates the field data for a drop-down list field using the provided UUID and droplist information.
     *
     * @param uuid - The unique identifier of the field to update. Can be a string or number.
     * @param droplist - The field type object containing updated properties for the drop-down list.
     *
     * @remarks
     * This method updates various properties of the field, such as type, options, validation, and API-related attributes,
     * by delegating to the global service's `updateField` method. It handles default values for optional properties and
     * ensures proper formatting for certain fields (e.g., converting the field type name to snake_case).
     */
    updateDropListFieldsData(uuid: string | number, droplist: FieldTypes, suppressRefresh: boolean = false): void {
        const droplistFieldName = (droplist?.name ?? '').toString();
        this._globalService.updateField(uuid, {
            selected_field_type_id: droplist.id,
            selected_type: droplistFieldName.toLowerCase().replace(/ /g, "_"),
            select_type_text: droplist.name,
            options: (droplist.id == 0 ? [] : droplist.options),
            field_info: droplist.field_info,
            multi_select: droplist?.multi_select,
            field_required: (droplist.field_required ?? 0),
            api_attribute: (droplist.api_attribute ?? ''),
            prefixid: droplist.id == 0 ? droplist.id : `d${droplist.id}`,
            selected_field_role_id: (droplist.role_id ?? ''),
            recipient_id: (droplist.role_data?.recipient_id ?? 0),
            field_section: droplist.field_section,
            validation: (droplist.validation ?? {}),
            name: droplist.name,
            description: (droplist?.description ?? ''),
            source: (droplist?.source ?? 'SimpleSign'),
            api_field: droplist?.api_field ?? 0,
            api_url: droplist?.api_url ?? '',
            api_key: droplist?.api_key ?? '',
            value: droplist?.value || null,
            is_locked: (droplist?.is_locked ? 1 : 0),
            content_library_selected_field_type: droplist?.key,
            content_library_selected_role: droplist?.role_id ?? '',
            secret_hidden_field: droplist.secret_hidden_field ?? 0,
            semi_hidden_field: droplist?.semi_hidden_field || 0,
            pinned_field: droplist?.pinned_field || 0
        }, () => { }, true);

        if (!suppressRefresh) {
            this._documentFieldsEmitter.patchData({ refresh: 1 });
        }
    }





    /**
    * Synchronizes the value of a text field (input or textarea) in the DOM and updates the corresponding field data.
    *
    * - Prevents updates if the field is locked.
    * - Updates the `.text-tag` element's text content within the field container.
    * - Sets the value of the input or textarea element and dispatches a `keyup` event to trigger any listeners.
    * - Automatically adjusts the height of the textarea to fit its content.
    * - Calls the global service to update the field's value.
    * - On blur events, performs field validation, applies field relations, and emits a patch event for the updated field.
    * - Emits an event to notify that fields data should be updated.
    *
    * @param $event - The input event triggered by user interaction.
    * @param documentfield - The field object representing the document field being edited.
    */
    syncTextFieldsData($event: InputEvent, documentfield: Field): void {
        if (documentfield.is_locked === 1) return;
        const value = ($event.target as HTMLInputElement)?.value ? ($event.target as HTMLInputElement)?.value : '';
        if (document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`)?.querySelector('.text-tag')) {
            document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`).querySelector('.text-tag').textContent = value;
        }

        const container = document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`);
        if (container) {
            const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
            if (input) {
                applyValueToField(documentfield.uuid, value, this._globalService, true);
            }
        }
        this.autoHeightForTextarea(document.querySelector(`.drag-fields[uuid='${documentfield.uuid}'] textarea`));
        this._globalService.updateField(documentfield.uuid, { value: ($event.target as HTMLInputElement)?.value }, (field: Field) => {
            if ($event.type == "blur") {
                onFieldValidation(field, this._globalService, field.selected_for_role_id);
                applyRelations(documentfield.uuid, this._globalService);
                this._documentFieldsEmitter.patchData({ uuid: documentfield.uuid, value: ($event.target as HTMLInputElement)?.value, refresh: 1 });
            }
        });
        this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [documentfield.uuid], deleted_field_uuid: [] });
        this.validationHandlerService.resetFieldHighlight(documentfield.uuid);
    }





    /**
     * Synchronizes the data of a date picker field with the application's state and UI.
     *
     * This method handles the following:
     * - Prevents updates if the field is locked.
     * - Retrieves and formats the date value from the input event.
     * - Updates the corresponding DOM element(s) with the formatted date or a generated tag text.
     * - Updates the global field state with the new value and its formatted alternative.
     * - Applies any field relations that may be affected by the change.
     * - Emits events to notify other parts of the application about the data update.
     *
     * @param $event - The input event triggered by the date picker field.
     * @param documentfield - The field object representing the date picker field being updated.
     * @returns A promise that resolves when the synchronization is complete.
     */
    async syncDatePickerFieldsData($event: InputEvent, documentfield: Field): Promise<void> {
        if (documentfield.is_locked === 1) return;
        const value = ($event.target as HTMLInputElement)?.value as string;
        const formatedDate = value ? await lastValueFrom(this._momentPipe$.transform(value, {} as UserTimezone) as Observable<string>) : '';
        const container = document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`);
        if (container) {
            if (documentfield.tag_view == "1") {
                const tagDate = container.querySelector('.tag-date');
                if (tagDate) {
                    tagDate.textContent = value ? formatedDate : this._globalService.generateTagText(documentfield.uuid);
                }
            } else {
                const datepickerField = container.querySelector('.datepicker-field') as HTMLInputElement;
                if (datepickerField) {
                    datepickerField.setAttribute('value', formatedDate);
                    datepickerField.value = formatedDate;
                }
            }
        }
        this._globalService.updateField(documentfield.uuid, { value: ($event.target as HTMLInputElement)?.value, alt_value: formatedDate });
        applyRelations(documentfield.uuid, this._globalService);
        this._documentFieldsEmitter.patchData({ uuid: documentfield.uuid, value: ($event.target as HTMLInputElement)?.value, refresh: 1 });
        this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [documentfield.uuid], deleted_field_uuid: [] });
        this.validationHandlerService.resetFieldHighlight(documentfield.uuid);
    }






    /**
     * Synchronizes the value of all drop list fields that share the same `selected_field_type_id` as the provided `documentfield`.
     * 
     * - If the field is locked (`is_locked === 1`), the function returns immediately.
     * - Finds all fields with the same `selected_field_type_id` and updates their displayed option label and value.
     * - Updates the DOM to reflect the new option label for each relevant field.
     * - Calls `applyRelations` and emits updates for each affected field.
     * - Finally, updates the original `documentfield` and emits the necessary update events.
     * 
     * @param option - The new value to set for the drop list fields.
     * @param documentfield - The field whose value is being synchronized across similar fields.
     */
    syncDropListFieldsData(optionValue: string, documentfield: Field): void {
        const option = documentfield.options.find(opt => opt.value == optionValue);
        if (documentfield.is_locked === 1) return;
            const selecedOptionLabel = document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`);
            selecedOptionLabel?.querySelectorAll('.selected-option-label').forEach((droplist: HTMLElement) => {
                this.renderer.setProperty(droplist, 'textContent', (documentfield.tag_view == "1" && !option?.name) ? this._globalService.generateTagText(documentfield.uuid) : option?.name)
            });
        this._globalService.updateField(documentfield.uuid, { value: option?.value, selected_option_id: option?.id }, () => {
            applyRelations(documentfield.uuid, this._globalService);
            this._documentFieldsEmitter.patchData({ uuid: documentfield.uuid, value: option?.value, refresh: 1 });
            generateCalculation(documentfield.uuid, this._globalService, true);
        });
        this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [documentfield.uuid], deleted_field_uuid: [] });
        this.validationHandlerService.resetFieldHighlight(documentfield.uuid);
    }




    /**
     * Synchronizes the data of a multi-select field based on the provided selection event.
     *
     * This method updates the field's value, manages the display of selected options,
     * and applies any related field relations. If an invalid option (empty string) is detected
     * in the selection, the field is reset and its display is cleared or updated with a tag text.
     * Otherwise, the selected values are set and displayed accordingly, either as a comma-separated
     * string (for tag types) or as HTML spans for each selected option.
     *
     * @param $event - The array of selected options from the droplist. Each option can be a string or a DroplistOptions object.
     * @param multiSelectField - The field object representing the multi-select field being updated.
     */
    syncMultiSelectFieldsData($event: DroplistOptions[], multiSelectField: Field): void {
        const selecedOptionLabel = document.querySelector(`.drag-fields[uuid='${multiSelectField.uuid}']`);
        const invalidOption = $event.some(option => typeof option === 'string' && option === "");
        if (invalidOption) {
            this._globalService.updateField(multiSelectField.uuid, { value: '' });
            selecedOptionLabel?.querySelectorAll('.selected-option-label').forEach((droplist: HTMLElement) => {
                this.renderer.setProperty(droplist, 'textContent', multiSelectField.tag_view == "1" ? this._globalService.generateTagText(multiSelectField.uuid) : '')
            });
            this._documentFieldsEmitter.patchData({ uuid: multiSelectField.uuid, value: '', refresh: 1 });
        } else {
            const refactoredDroplistValue = this._globalService.refactorDroplistValue(($event as string[]), multiSelectField);
            this._globalService.updateField(multiSelectField.uuid, { value: refactoredDroplistValue.selectedValue, selected_option_id: refactoredDroplistValue.selectedOptionid });
            const displayContent = multiSelectField.type_of === 'tag'
                ? refactoredDroplistValue.selectedNames.join(', ')
                : refactoredDroplistValue.selectedNames.map(v => `<span class="multiselect-selected-options">${v}</span>`).join('');

            const isTagViewEnabled = multiSelectField.tag_view == "1" && !displayContent;
            const content = isTagViewEnabled
                ? this._globalService.generateTagText(multiSelectField.uuid)
                : displayContent;

            selecedOptionLabel?.querySelectorAll('.selected-option-label').forEach((droplist: HTMLElement) => {
                this.renderer.setProperty(droplist, 'innerHTML', content);
            });

        }
        applyRelations(multiSelectField.uuid, this._globalService);
        this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [multiSelectField.uuid], deleted_field_uuid: [] });
        this.validationHandlerService.resetFieldHighlight(multiSelectField.uuid);
    }






    /**
     * Synchronizes the UI and internal data model for a multi-choice document field based on user selection.
     *
     * Handles both single and multiple choice scenarios, updating the field's value, UI state (active classes),
     * and triggering related updates and events. If the field is locked, no action is taken.
     *
     * @param $event - The selected option(s) from the droplist. Can be a single `DroplistOptions` object or an array of them.
     * @param multichoiceField - The field object representing the multi-choice field to update.
     *
     * @remarks
     * - If `multichoiceField.is_locked` is `1`, the function returns early and does nothing.
     * - For single choice (`multi_choice == "0"`), updates the field with the selected option's name.
     * - For multiple choice (`multi_choice == "1"`), updates the field with an array of selected option names.
     * - Updates the UI by toggling the `multiChoice-active` class on the relevant DOM elements.
     * - Calls `applyRelations` to handle any field dependencies.
     * - Emits updates to other parts of the application via `_documentFieldsEmitter` and `_documentHelperFacade`.
     */
    syncDocumentListFieldsData($event: DroplistOptions | DroplistOptions[], multichoiceField: Field): void {
        if (multichoiceField.is_locked === 1) return;
        const container = document.querySelector(`.drag-fields[uuid='${multichoiceField.uuid}']`);
        const items = container.querySelectorAll('.multiChoice-item');
        if (container) {
            items.forEach(item => item.classList.remove('multiChoice-active'));
        }
        if (multichoiceField.multi_choice == "0" && !Array.isArray($event)) {
            const option = multichoiceField.options.find(opt => opt.value == $event);
            if(option){
                this.renderer.addClass(container.querySelector(`.multiChoice-item[option_uuid='${option.id}']`), 'multiChoice-active');
                this._globalService.updateField(multichoiceField.uuid, { value: option.value, selected_option_id: option.id });
                applyRelations(multichoiceField.uuid, this._globalService);
                this._documentFieldsEmitter.patchData({ uuid: multichoiceField.uuid, value: option.value, refresh: 1 });
            }
        }
        else if (multichoiceField.multi_choice == "1" && Array.isArray($event)) {
            const isInvalidOption = $event.some(option => typeof option === 'string' && option === "");
            const refactoredDroplistValue = this._globalService.refactorDroplistValue(($event as string[]), multichoiceField);
            if (isInvalidOption) {
                $(`[uuid='${multichoiceField.uuid}']`)?.find('.multiChoice-item')?.removeClass('multiChoice-active');
                this._globalService.updateField(multichoiceField.uuid, { value: '' });
            } else {
                for (const optionId of refactoredDroplistValue.selectedOptionid) {
                    this.renderer.addClass(container.querySelector(`.multiChoice-item[option_uuid='${optionId}']`), 'multiChoice-active');
                }
                this._globalService.updateField(multichoiceField.uuid, { value: refactoredDroplistValue.selectedValue, selected_option_id: refactoredDroplistValue.selectedOptionid });
            }
            applyRelations(multichoiceField.uuid, this._globalService);
            this._documentFieldsEmitter.patchData({ uuid: multichoiceField.uuid, value: refactoredDroplistValue.selectedValue, refresh: 1 });
        }
        this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [multichoiceField.uuid], deleted_field_uuid: [] });
        this.validationHandlerService.resetFieldHighlight(multichoiceField.uuid);
    }





    /**
     * Sync document checkbox field to content
     * @param $event - The change event containing the new value.
     * @param documentfield - The document field to update.
     * @returns {void}
     * @example
     * ```typescript
     * this.syncDocumentCheckBoxFieldsData({ name: 'Checkbox 1', value: 1 }, { uuid: '1234-5678-9012' });
     * ```
     */
    syncDocumentCheckBoxFieldsData($event: { name: string, value: 0 | 1 }, documentfield: Field): void {
        if (documentfield.is_locked === 1) return;
        this._globalService.updateField(documentfield.uuid, { value: $event.value }, () => {
            this._documentHelperFacade.emitShouldUpdateFieldsData({ uuid: [documentfield.uuid], deleted_field_uuid: [] });
        });
        applyRelations(documentfield.uuid, this._globalService);
        this._globalService.toggleClass(document.querySelector(`.drag-fields[uuid='${documentfield.uuid}']`), 'checkbox-prechecked');
        this._documentFieldsEmitter.patchData({ uuid: documentfield.uuid, value: $event.value, refresh: 1 });
        this.validationHandlerService.resetFieldHighlight(documentfield.uuid);
    }





    /**
     * Processes and updates field data based on the provided metadata and field visibility type.
     * 
     * This method performs several checks to prevent duplicate or conflicting fields:
     * - Checks if the field already exists as a secret hidden field or semi hidden field and shows a warning if so.
     * - Checks if the field exists in the document and warns the user.
     * - If the field is a duplicate, prompts the user for confirmation before replacing its configuration.
     * 
     * Depending on the `meta_data_type` of the result, constructs the appropriate field object.
     * Updates the global fields data and triggers related field updates and integration logic if necessary.
     * 
     * @param result - The metadata describing the field to be processed.
     * @param fieldVisibilityType - The visibility configuration for the field.
     * @returns A promise that resolves when the field data has been processed and updated.
     */
    async proceedFieldsDataToUpdate(result: ISearcableMetaData, fieldVisibilityType: IFieldVisibilityType, value: string = ''): Promise<void> {
        const uuid = UUID();
        let field = {};
        const commonProps = this._fieldsBuilderService.getCommonProps(uuid,
            {
                ...result,
                uuid: uuid,
                id: result.id,
                secret_hidden_field: fieldVisibilityType.is_secret_hidden_field,
                semi_hidden_field: fieldVisibilityType.is_semi_hidden_field,
                pinned_field: fieldVisibilityType.is_pinned_field,
                field_section: FieldInfo.DOCUMENT_FIELD,
                select_type_text: result.meta_data_name,
                name: result.meta_data_name,
                value: value,
                order: this._globalService.getMaxFieldOrder()
            }
        );

       
        const secretField = this._globalService.findMatchingField(this._globalService.fields_data, 'selected_field_type_id', result.id, commonProps.field_section)?.some(f => f.secret_hidden_field === 1);
        if (secretField) {
            this._globalService.toastrWarning(
                this._translocoService.translate('Adding existing field in document toaster message (Template/Document settings - section) - This field already exists in field', {fieldName: 'secret'})
            );
            return;
        }
        
        
        const existingField = this._globalService.findMatchingField(this._globalService.fields_data, 'selected_field_type_id', result.id, commonProps.field_section)?.find(f => f.semi_hidden_field === 1 || f.pinned_field === 1);
        if (existingField) {
            const field = existingField.semi_hidden_field === 1 ? 'semi hidden' : 'pinned';
            this._globalService.toastrWarning(
                this._translocoService.translate('Adding existing field in document toaster message (Template/Document settings - section) - This field already exists in field', {fieldName: field})
            );
            return;
        }


        const isFieldExistsInContent = this._globalService.findFieldsWithCondition(this._globalService.fields_data, 'selected_field_type_id', result.id, commonProps.field_section) as Field[];
        isFieldExistsInContent.forEach((field: Field) => {
            if(!commonProps?.pinned_field && this._fieldRendererService.isRendered(field.uuid)) {
                this._globalService.toastrWarning(this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - This field already exists in the document'));
                return;
            }
        });

        const isDuplicateField = this._globalService.findFieldsWithCondition(this._globalService.fields_data, 'selected_field_type_id', result.id, commonProps.field_section)?.filter(field => !field.pinned_field);
        if (!commonProps?.pinned_field && isDuplicateField.length > 0) {
            const resultSwal = await Swal.fire({
                title: this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - Field already exists'),
                html: `${this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - This field is already added to the document')}<br>${this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - Adding it here will replace its current configuration')}<br><br>${this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - Are you sure you want to continue')}`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - Yes, continue'),
                cancelButtonText: this._translocoService.translate('Adding semi hidden field warning text (Edit template settings - Secret fields) - Cancel')
            });
            if (!resultSwal.isConfirmed) {
                return;
            }
        }

        switch (result.meta_data_type) {
            case 'text': {
                field = this._fieldsBuilderService.extractTextFieldObject(commonProps)
                break;
            }
            case 'droplist': {
                field = this._fieldsBuilderService.extractDroplistFieldObject({ ...commonProps, options: result.meta_data_option })
                break;
            }
            case 'datepicker': {
                field = this._fieldsBuilderService.extractDatePickerFieldObject(commonProps)
                break;
            }
            case 'checkbox': {
                field = this._fieldsBuilderService.extractCheckboxFieldObject(commonProps)
                break;
            }
        }
        this._globalService.fields_data = {
            ...this._globalService.fields_data,
            [uuid]: {...field}
        };
        this.updateRelatedFields(uuid, result, this._globalService.fields_data[uuid]);
        if (isDuplicateField.length > 0) {
            this._integrationService.fillingTag(this._globalService.fields_data[uuid]);
        }
    }

    



    /**
     * This method updates the input fields, dropdown lists, date pickers, or checkboxes
     * based on the type of metadata provided. It handles different types of metadata
     * such as 'text', 'droplist', 'datepicker', and 'checkbox'. For
     * each type, it calls the appropriate method to update the fields.
     * @param uuid - The unique identifier for the field to be updated.
     * @param result - The metadata containing the updated field information.
     * @returns void
     * @example 
     * ```typescript
     * this.updateRelatedFields('1234-5678-9012', { 
     *   meta_data_type: 'text',  
     *  meta_data_name: 'Sample Text',
     *  id: 'abcd-efgh-ijkl', 
     *  meta_data_option: []
     *  });
     */
    private updateRelatedFields(uuid: string, result: ISearcableMetaData, field: Field): void {
        const isInputField = ['text', 'datepicker', 'checkbox'].includes(result.meta_data_type);
        if (isInputField) {
            this.updateInputFieldsData(uuid, { ...field, id: result.id }, false, `d${result.id}`);
        } else {
            this.updateDropListFieldsData(uuid, { ...field, id: result.id });
        }
    }





    /**
     * Extracts and returns an array of `Field` objects from the global fields data,
     * optionally filtering out duplicate fields based on the `skipFilteration` flag.
     *
     * @param skipFilteration - If `true`, skips filtering for duplicates and includes all fields.
     *                          If `false`, filters out duplicate fields using the global service.
     * @returns An array of `Field` objects that meet the following criteria:
     *   - If `skipFilteration` is `true`, includes fields where `field_info` is `'document_field'`.
     *   - Otherwise, includes fields where:
     *       - `field_info` is `'document_field'` or `'custom_field'`
     *       - `selected_for_role_id` is `0` or `-1`
     *       - `type` is not `'signature'`
     */
    extractFieldsData(skipFilteration: boolean): Field[] {
        const fields_data: Field[] = [];
        const fieldsData = skipFilteration === false ? this._globalService.filterDuplicates(this._globalService.fields_data) : this._globalService.fields_data;
        for (const key in fieldsData) {
            if (Object.prototype.hasOwnProperty.call(fieldsData, key)) {
                const field = fieldsData[key];
                const shouldPush =
                    (skipFilteration && field.field_info == 'document_field') ||
                    (
                        (field.field_info == 'document_field' || field.field_info == 'custom_field') &&
                        field.type != 'signature'
                    );
                if(!field?.uuid){
                    field.uuid = key;
                }
                if (shouldPush) {
                    fields_data.push(field);
                }
            }
        }
        return fields_data;
    }




    /**
     * Auto height for textarea 
     * @param element which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript
     * this.autoHeightForTextarea(document.querySelector('.textarea'));
     * ``` 
     */
    autoHeightForTextarea(element: HTMLElement) {
        if (element) {
            element.style.height = (element.scrollHeight) + "px";
        }
    }








   
    /**
     * Restructures the provided pinned fields by updating or creating corresponding fields in the global service.
     *
     * - Filters the `pinnedFields` array to include only those with a status of `1`.
     * - For each filtered pinned field:
     *   - Attempts to find matching fields in the global service based on `selected_field_type_id` and the pinned field's metadata ID.
     *   - If no matching fields are found, creates or updates a field with the pinned field's metadata and value.
     *   - If matching fields exist, updates each field to mark it as pinned and not hidden.
     * - Emits a refresh event to update document fields.
     * - Optionally executes a callback function after restructuring is complete.
     *
     * @param pinnedFields - An array of pinned field objects to be restructured.
     * @param callback - An optional callback function to be executed after restructuring is complete.
     */
    reStructureFields(pinnedFields: IField[], callback?: () => void): void {
        (pinnedFields ?? [])
            .filter(f => f.status == true).forEach(pinnedField => {
                if (!pinnedField.meta_data) {
                    return; // continue to next item
                }
               const matchingFields = this._globalService.findMatchingField(
                    this._globalService.fields_data,
                    'selected_field_type_id',
                    pinnedField?.meta_data?.id,
                    'document_field'
                ) ?? [];
                if (!matchingFields?.length) {
                    const existingFieldWithValue = matchingFields.find(f => f.value && f.value !== "");
                    this.proceedFieldsDataToUpdate(
                        pinnedField.meta_data,
                        {
                            is_secret_hidden_field: 0,
                            is_semi_hidden_field: 0,
                            is_pinned_field: 1
                        },
                        existingFieldWithValue?.value || ''
                    );
                } else {
                    matchingFields.forEach(field => {
                        this._globalService.updateField(
                            field.uuid,
                            {
                                pinned_field: 1,
                                is_secret_hidden_field: 0,
                                is_semi_hidden_field: 0
                            }
                        )
                    });
                }
            });
            this._documentFieldsEmitter.patchData({refresh: 1});
            if(callback){
                callback();
            }
    }





    extractSectionFromField(field: Field): string {
        if (field.pinned_field === 1) {
        return 'pinned_field';
        } else if (field.semi_hidden_field === 1) {
        return 'semi_hidden_field';
        } else if (field.secret_hidden_field === 1) {
        return 'secret_hidden_field';
        }
        return 'document_field';
    }



      /**
   * Formats a section identifier into a human-readable section name.
   *
   * Converts specific section keys to their corresponding display names:
   * - 'document_field' => 'Document Field'
   * - 'semi_hidden_field' => 'Semi Hidden'
   * - 'secret_field' => 'Secret Field'
   * For any other input, returns the original section string.
   *
   * @param section - The section identifier to format.
   * @returns The formatted, human-readable section name.
   */
  formatSectionName(section: string): string {
    switch (section) {
      case 'document_field': return 'Document Field';
      case 'semi_hidden_field': return 'Semi Hidden Field';
      case 'secret_hidden_field': return 'Secret Hidden Field';
      case 'pinned_field': return 'Highlighted Field';
      default: return section;
    }
  }



}